import { isArray, isEmpty } from 'radash';
import { useController } from 'react-hook-form';
import { useShallow } from 'zustand/react/shallow';

import { Textarea as CoreTextarea } from '@/components/core/Textarea';

import useFocusOnAttribute from '../hooks/useFocusOnAttribute';
import { useIsAttributeReadonly } from '../hooks/useIsAttributeReadonly';
import { useIsHiddenAttribute } from '../hooks/useIsHiddenAttribute';
import { useLengthMessage } from '../hooks/useLengthMessage';
import { usePlaceholder } from '../hooks/usePlaceholder';
import { useValidationsStore,Validation } from '../hooks/useValidationsStore';
import { Layout } from '../Layout/Layout';
import { EditorComponent } from '../types';

interface TextareaComponentProps extends EditorComponent {
    // String specific props go here
    className?: string;
    isDimensional?: boolean;
}

const TextareaComponent = ({
    attribute,
    direction = 'horizontal',
    fieldPath = attribute.name,
    hasErrors: parentHasErrors = false,
    hasWarnings: parentHasWarnings = false,
    hidden = false,
    readonly = false,
    hideLabel = false,
    isKeyAttribute = false,
    isDimensional = false,
    beforeAttributeControl,
    className,
    handleUrlChange,
    onBlur: parentOnBlur
}: TextareaComponentProps) => {

    const focusRef = useFocusOnAttribute<HTMLTextAreaElement>(attribute);

    const placeholder = usePlaceholder(attribute, readonly);

    const validationErrors = useValidationsStore(useShallow(state => state.validationErrors[fieldPath] as Validation[]));
    const validationWarnings = useValidationsStore(useShallow(state => state.validationWarnings[fieldPath] as Validation[]));
    const hasErrors = parentHasErrors || !isEmpty(validationErrors);
    const hasWarnings = parentHasWarnings || !isEmpty(validationWarnings);

    hidden = useIsHiddenAttribute(attribute, attribute.name, hidden);

    readonly = useIsAttributeReadonly(attribute, attribute.name, readonly);

    const { field } = useController({
        name: fieldPath,
    });
    const currentValue = field.value ?? '';

    const lengthMessage = useLengthMessage(String(currentValue).length, attribute.params?.length);

    const getNumberOfRows = () => {

        const renderer = attribute.params?.renderer || attribute.params?.customRenderer;
        const rendererParams: { [key:string]: string | number } = {};

        if (isArray(renderer)) {
            renderer.forEach(renderingParam => rendererParams[renderingParam.key] = renderingParam.value);
        }

        return rendererParams['rows'] as number || 5;
    };

    if (hidden) {
        return null;
    }

    const setRef = (e: HTMLTextAreaElement | null) => {
        if (e) {
            (focusRef as React.MutableRefObject<HTMLTextAreaElement | null>).current = e;
        }
        field.ref(e);
    };

    const handleBlur = () => {
        field.onBlur();
        if (parentOnBlur) {
            parentOnBlur();
        }
    };

    return (
        <Layout
            attribute={attribute}
            hideLabel={hideLabel}
            direction={direction}
            fieldPath={fieldPath}
            gridMode={false}
            isKeyAttribute={isKeyAttribute}
            beforeAttributeControl={beforeAttributeControl}
            isClearable={!readonly && !isDimensional}
            labelVerticalAlign="top"
        >
            <div className="tw-flex tw-flex-row">
                <CoreTextarea
                    id={fieldPath}
                    placeholder={placeholder}
                    onClick={handleUrlChange}
                    rows={getNumberOfRows()}
                    name={field.name}
                    value={currentValue}
                    onChange={field.onChange}
                    onBlur={handleBlur}
                    disabled={readonly}
                    ref={setRef}
                    className={`${hasErrors ? 'error-input' : hasWarnings ? 'warning-input' : ''} ${className}`}
                />

                { attribute.params?.uiShowLength &&
                    <div className="input lg-font-dynamic tw-text-t-primary tw-max-h-[40px] tw-px-3 tw-flex tw-items-center tw-bg-gray-100 tw-whitespace-nowrap">
                        {lengthMessage}
                    </div>
                }
            </div>
        </Layout>
    );
};

export { TextareaComponent };
